import { Button, Divider, Skeleton, Stack, Typography } from '@mui/material'
import numeral from 'numeral'
import { Fragment, useMemo, useState } from 'react'
import { Icons, InformationModal } from 'utoniq-ui'

import {
  ItemResaleRaffleUserDetailFragment,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { BaseLayout } from '@/components/layouts'
import {
  ResaleItem,
  ResaleRaffleStatus,
  ResaleTag,
} from '@/components/organisms'
import { NotFound } from '@/components/pages/Errors/NotFound'
import {
  DetailSkeleton,
  StatusSkeleton,
} from '@/components/pages/ResaleDetail/template'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { encodeSkuId, getItemSkuPrice } from '@/core/sku'

export * from './fragments'

export type State = {
  loading: boolean
  submitting: boolean
  isError?: boolean
  itemResaleRaffleUser: Maybe<ItemResaleRaffleUserDetailFragment> | undefined
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onCancel: () => Promise<void>
  onPay: () => void
}

export const ResaleRaffleDetailTemplate: Template<State, Action> = ({
  state,
  onCancel,
  onPay,
}) => {
  const { t } = useTranslation('resaleRaffleDetail')
  const [openConfirmCancel, setOpenConfirmCancel] = useState<boolean>(false)

  if (!state.loading && !state.itemResaleRaffleUser) {
    return <NotFound useNextRouter={false} />
  }

  return (
    <BaseLayout
      customNavigation={
        <FixedBottomCell
          status={state.itemResaleRaffleUser?.status}
          onClickCancel={() => setOpenConfirmCancel(true)}
          onClickPay={onPay}
          loading={state.loading}
        />
      }
      margin
      header={{
        back: true,
        title: t(
          state.itemResaleRaffleUser?.status === ResaleRaffleStatus.Purchased
            ? 'purchasedTitle'
            : 'title'
        ),
      }}
    >
      {state.loading ? (
        <>
          <StatusSkeleton />
          <DetailSkeleton />
        </>
      ) : (
        <>
          <StatusCell
            isError={state.isError}
            itemResaleRaffleUser={state.itemResaleRaffleUser}
            onClickPay={onPay}
          />
          <DetailCell itemResaleRaffleUser={state.itemResaleRaffleUser} />
          <InformationModal
            open={openConfirmCancel}
            onClose={() => setOpenConfirmCancel(false)}
            title={t('confirmCancelListingTitle')}
            content={t('confirmCancelListingDesc')}
            buttons={
              <Button
                size="large"
                sx={{ width: 212 }}
                onClick={onCancel}
                loading={state.submitting}
              >
                {t('confirmCancel')}
              </Button>
            }
            cancelText={t('close')}
          />
        </>
      )}
    </BaseLayout>
  )
}

type StatusCellProps = {
  isError: State['isError']
  itemResaleRaffleUser: State['itemResaleRaffleUser']
  onClickPay: () => void
}
const StatusCell: Cell<StatusCellProps> = ({
  isError,
  itemResaleRaffleUser,
  onClickPay,
}) => {
  const { t } = useTranslation('resaleRaffleDetail')
  return (
    <CellFrame sx={{ py: 7 }}>
      <Stack
        direction="row"
        justifyContent="center"
        spacing={3}
        alignItems="center"
      >
        {isError ? (
          <>
            <Icons.Error sx={{ color: 'error.main' }} />
            <Typography variant="h6">{t('winningErrorStatus')}</Typography>
          </>
        ) : (
          <>
            {itemResaleRaffleUser?.status === ResaleRaffleStatus.Win ? (
              <Icons.Error sx={{ color: 'error.main' }} />
            ) : (
              <Icons.Check sx={{ color: 'primary.main' }} />
            )}
            <Typography variant="h6">
              {t(`${itemResaleRaffleUser?.status}Status`)}
            </Typography>
          </>
        )}
      </Stack>
      {!!t(`${itemResaleRaffleUser?.status}StatusDesc`) && (
        <Stack sx={{ pt: 2 }}>
          <Typography
            variant="subtitle1"
            color={
              itemResaleRaffleUser?.status === ResaleRaffleStatus.Win
                ? 'text.primary'
                : 'text.secondary'
            }
            align="center"
            sx={{
              fontWeight:
                itemResaleRaffleUser?.status === ResaleRaffleStatus.Win
                  ? 'bold'
                  : 'normal',
            }}
          >
            {isError
              ? t('winningErrorDesc')
              : t(`${itemResaleRaffleUser?.status}StatusDesc`, {
                  updatedAt: String(itemResaleRaffleUser?.formattedUpdatedAt),
                })}
          </Typography>
        </Stack>
      )}
      {itemResaleRaffleUser?.status === ResaleRaffleStatus.Win && (
        <Stack sx={{ pt: 2 }}>
          <Stack
            sx={{
              my: 7,
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: 'error.main',
            }}
          >
            <Typography
              variant="body2"
              color="error.main"
              sx={{ py: 2 }}
              align="center"
            >
              {t('repaymentExpirationDate', {
                date: itemResaleRaffleUser.resaleItems[0]
                  ?.formattedPaymentExpiredAt,
              })}
            </Typography>
          </Stack>
          <Stack alignItems="center">
            <Button
              variant="contained"
              size="large"
              sx={{ width: 244 }}
              onClick={onClickPay}
            >
              {t('repayment')}
            </Button>
          </Stack>
        </Stack>
      )}
    </CellFrame>
  )
}

export const DetailCell = ({
  itemResaleRaffleUser,
}: {
  itemResaleRaffleUser: State['itemResaleRaffleUser']
}) => {
  const { t } = useTranslation('resaleRaffleDetail')
  const { generateQueryPath, routerPages } = useRouter()
  const { price, formattedPrice } = useMemo(
    () => getItemSkuPrice(itemResaleRaffleUser?.item),
    [itemResaleRaffleUser?.item]
  )

  const data = [
    {
      label: t('ticketPrice'),
      value: `￥${numeral(itemResaleRaffleUser?.totalItemPrice).format('0,0')}`,
    },
    {
      // 振り込み手数料
      label: t('transferFee'),
      value: `¥${numeral(
        itemResaleRaffleUser?.item?.metadata?.ticket?.resaleFee
          ?.bankTransferFeePerOrder
      ).format('0,0')}`,
    },
  ]
  const totalAmount = useMemo(() => {
    if (!price) {
      return 0
    }
    return numeral(
      price * (itemResaleRaffleUser?.itemCount || 1) +
        (itemResaleRaffleUser?.item?.metadata?.ticket?.resaleFee
          ?.bankTransferFeePerOrder || 0)
    ).format('0,0')
  }, [
    itemResaleRaffleUser?.item?.metadata?.ticket?.resaleFee
      ?.bankTransferFeePerOrder,
    itemResaleRaffleUser?.itemCount,
    price,
  ])

  return (
    <CellFrame>
      <ResaleItem title={t('ticketInfo', 'チケット情報')} Icon={Icons.Post}>
        <Stack spacing={2}>
          <Stack spacing={1}>
            <Typography variant="h5">
              {itemResaleRaffleUser?.item?.title}
            </Typography>
            {!!itemResaleRaffleUser?.item?.sku?.unitName && (
              <Typography variant="subtitle1">
                {itemResaleRaffleUser?.item?.sku?.unitName}
              </Typography>
            )}
          </Stack>

          <ResaleTag status={itemResaleRaffleUser?.status ?? ''} />
          <Typography variant="body2" sx={{ pt: 3 }}>
            {itemResaleRaffleUser?.item?.metadata?.ticket?.description}
          </Typography>
        </Stack>
      </ResaleItem>
      <ResaleItem title={t('exhibitionInfo', '応募情報')}>
        <Stack spacing={4}>
          <Stack spacing={1}>
            <Typography variant="subtitle1">
              {t('ticketPrice', '価格')}
            </Typography>
            <Typography variant="body2">
              ¥{formattedPrice}/1{t('perSheet')}
            </Typography>
          </Stack>
          <Stack spacing={1}>
            <Typography variant="subtitle1">
              {t('holdingQuantity', '応募枚数')}
            </Typography>
            <Typography variant="body2">
              {itemResaleRaffleUser?.itemCount}
              {t('sheet')}
            </Typography>
          </Stack>
          <Stack spacing={1}>
            <Typography variant="subtitle1">
              {t('appliedAt', '応募日時')}
            </Typography>
            <Typography variant="body2">
              {itemResaleRaffleUser?.formattedCreatedAt}
            </Typography>
          </Stack>
        </Stack>
      </ResaleItem>
      {[
        ResaleRaffleStatus.Applied,
        ResaleRaffleStatus.Win,
        ResaleRaffleStatus.Purchased,
      ].includes(itemResaleRaffleUser?.status as ResaleRaffleStatus) && (
        <ResaleItem title={t('paymentResalePrice', 'お支払い総額')}>
          <Stack sx={{ pb: 3 }}>
            {data.map(({ value, label }, index) => (
              <Fragment key={index}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ py: 2 }}
                >
                  <Typography variant="subtitle1" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="body2">{value}</Typography>
                </Stack>
                <Divider />
              </Fragment>
            ))}
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ py: 2 }}
            >
              <Typography variant="subtitle1" color="text.secondary">
                {t('total')}
              </Typography>
              <Typography variant="h6">¥{totalAmount}</Typography>
            </Stack>
          </Stack>
        </ResaleItem>
      )}
      {itemResaleRaffleUser?.status === ResaleRaffleStatus.Purchased && (
        <Stack alignItems="center" spacing={4} sx={{ pb: 6 }}>
          <Button
            size="large"
            href={generateQueryPath(
              routerPages.OwnedItemDetail,
              { itemId: itemResaleRaffleUser?.item.id },
              { skuId: encodeSkuId(itemResaleRaffleUser?.skuId ?? '') }
            )}
            sx={{ width: 244, margin: 'auto' }}
            color="primary"
          >
            {t('ticketDetail')}
          </Button>
        </Stack>
      )}
    </CellFrame>
  )
}

type FixedBottomCellProps = {
  onClickCancel: () => void
  onClickPay: () => void
  status?: string
  loading: boolean
}
const FixedBottomCell: Cell<FixedBottomCellProps> = ({
  onClickCancel,
  onClickPay,
  status,
  loading,
}) => {
  const { t } = useTranslation('resaleRaffleDetail')

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 4, bgcolor: 'grey.50' }}>
        <Skeleton
          variant="rectangular"
          sx={{ width: 244, height: 44, borderRadius: 34 }}
        />
      </Stack>
    )
  }

  if (
    [
      ResaleRaffleStatus.Lost,
      ResaleRaffleStatus.Invalid,
      ResaleRaffleStatus.Canceled,
      ResaleRaffleStatus.Purchased,
    ].includes((status || '') as ResaleRaffleStatus)
  ) {
    return null
  }

  return (
    <Stack alignItems="center" sx={{ py: 4, bgcolor: 'grey.50' }}>
      <Button
        variant={
          status === ResaleRaffleStatus.Applied ? 'outlined' : 'contained'
        }
        size="large"
        sx={{ width: 244 }}
        onClick={
          status === ResaleRaffleStatus.Applied ? onClickCancel : onClickPay
        }
      >
        {status === ResaleRaffleStatus.Applied
          ? t('cancelListing', '抽選をキャンセルする')
          : t('repayment')}
      </Button>
    </Stack>
  )
}
